/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import UnitChooser, {UnitOption} from './UnitChooser';

export interface OdometerUnitChooserProps {
  defaultValue?: 'Default' | 'mi' | 'km';
  readOnly?: boolean;
  onChange?: (val: string) => void;
  required?: boolean;
  error?: string;
  hasError?: boolean;
};

/**
 * @OdometerUnitChooser
 *
 * @param {boolean} hasError If the field has an error the value is passed on
 *   to the dropdown component
 * @param {string | object} defaultValue Default value passed to the component.
 *   Will only be considered if passed as OdometerUnitOption.
 * @param {boolean} readOnly If true then dropdown is disabled
 * @param {Function} onChange Callback fired with the selected value when a
 *   selection is made
 *
 * ```typescript
 *
 *   import {OdometerUnitChooser} from '@ion/dls/src/editors/';
 *
 * ```
 *
 * <OdometerUnitChooser defaultValue='mi'
 *   hasError={true}
 *   readOnly={false}
 *   onChange={handleChange} />
 */
export const OdometerUnitChooser = ({
  hasError,
  defaultValue,
  readOnly,
  onChange
}: OdometerUnitChooserProps) => {
  const options: UnitOption[] = [
    {
      label : translate('common:units.default'),
      value : 'Default'
    },
    {
      label : translate('common:units.mi'),
      value : 'mi'
    },
    {
      label : translate('common:units.km'),
      value : 'km'
    }
  ];

  return (
    <UnitChooser options={options}
      fieldLabel={translate('common:editors.odometerReferenceUnit.label')}
      hasError={hasError}
      defaultValue={defaultValue}
      readOnly={readOnly}
      onChange={onChange} />
  );
};

export default OdometerUnitChooser;
